import { useEffect, useMemo, useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import { reaction } from 'mobx'
import * as THREE from 'three'
import {
    environmentSideOffset,
    environmentSpacing,
    environmentStartOffset,
} from '../../config/constants'
import { applyHeightFogToObject } from '../../materials/heightFog'
import { createInstancedAssetGroup } from '../../../utils/instancedAsset'
import type { TrackController, TrackRegionState } from '../track/trackController'
import { useTrackController } from '../track/useTrackController'
import { useRunState } from '../../provider/useRunState'
import { gltfAsset } from '../../assetStore'
import { applyMaterial, useAtlasMaterial } from '../../materials/atlasMaterial'
import { disposeObject } from '../../../utils/threeDisposal'
import {
    createWorldMatrix,
    customPlatformAssemblies,
    customStartPropAssetKeys,
    customPlatformPropPlacements,
    individualContentPlatformPlacements,
    type CustomStartPropAssetKey,
} from './IndividualPlatforms'
import { lerp } from 'three/src/math/MathUtils.js'

export function Platforms() {
    const { scene: cloudScene } = useGLTF(gltfAsset.Cloud)
    const { scene: lvl04BridgeScene } = useGLTF(gltfAsset.Lvl04Bridge)
    const { scene: lvl04PirsmAScene } = useGLTF(gltfAsset.Lvl04PirsmA)
    const { scene: lvl04PirsmBScene } = useGLTF(gltfAsset.Lvl04PirsmB)
    const { scene: lvl04SpaceDonutScene } = useGLTF(gltfAsset.Lvl04SpaceDonut)
    const { scene: lvl04SpaceHeartScene } = useGLTF(gltfAsset.Lvl04SpaceHeart)
    const { scene: lvl04StarAScene } = useGLTF(gltfAsset.Lvl04StarA)
    const { scene: lvl04StarBScene } = useGLTF(gltfAsset.Lvl04StarB)
    const { scene: lvl2ClockScene } = useGLTF(gltfAsset.LVL2_Clock)
    const { scene: lvl2GeometriesScene } = useGLTF(gltfAsset.LVL2_Geometries)
    const { scene: lvl2GlobeScene } = useGLTF(gltfAsset.LVL2_Globe)
    const { scene: lvl2MathScienceScene } = useGLTF(gltfAsset.LVL2_MathScience)
    const { scene: lvl3FolderScene } = useGLTF(gltfAsset.LVL3_Folder)
    const { scene: lvl3PaperplaneScene } = useGLTF(gltfAsset.LVL3_Paperplane)
    const { scene: lvl3Window1Scene } = useGLTF(gltfAsset.LVL3_Window1)
    const { scene: lvl3Window2Scene } = useGLTF(gltfAsset.LVL3_Window2)
    const { scene: rainbowScene } = useGLTF(gltfAsset.Rainbow)
    const { scene: teddyScene } = useGLTF(gltfAsset.Teddy)
    const { scene: toyCarScene } = useGLTF(gltfAsset.ToyCar)
    const { scene: level1Scene } = useGLTF(gltfAsset.Platform_LVL_01)
    const { scene: level2Scene } = useGLTF(gltfAsset.Platform_LVL_02)
    const { scene: level3Scene } = useGLTF(gltfAsset.Platform_LVL_03)
    const { scene: level4Scene } = useGLTF(gltfAsset.Platform_LVL_04)
    const { scene: level1ContentSceneA } = useGLTF(gltfAsset.PlatformContent_LVL01_A)
    const { scene: level1ContentSceneB } = useGLTF(gltfAsset.PlatformContent_LVL01_B)
    const { scene: level1ContentSceneC } = useGLTF(gltfAsset.PlatformContent_LVL01_C)
    const { scene: level2ContentSceneA } = useGLTF(gltfAsset.PlatformContent_LVL02_A)
    const { scene: level2ContentSceneB } = useGLTF(gltfAsset.PlatformContent_LVL02_B)
    const { scene: level2ContentSceneC } = useGLTF(gltfAsset.PlatformContent_LVL02_C)
    const { scene: level3ContentSceneA } = useGLTF(gltfAsset.PlatformContent_LVL03_A)
    const { scene: level3ContentSceneB } = useGLTF(gltfAsset.PlatformContent_LVL03_B)
    const { scene: level3ContentSceneC } = useGLTF(gltfAsset.PlatformContent_LVL03_C)
    const { scene: level4ContentSceneA } = useGLTF(gltfAsset.PlatformContent_LVL04_A)
    const { scene: level4ContentSceneB } = useGLTF(gltfAsset.PlatformContent_LVL04_B)
    const { scene: level4ContentSceneC } = useGLTF(gltfAsset.PlatformContent_LVL04_C)
    const trackController = useTrackController()
    const runState = useRunState()
    const unlockedRegionsRef = useRef<Set<1 | 2 | 3 | 4>>(new Set([1]))
    const atlasMaterial = useAtlasMaterial()

    const platformGroup = useMemo(
        () => createPlatforms(
            {
                1: applyMaterial(level1Scene, atlasMaterial),
                2: level2Scene,
                3: applyMaterial(level3Scene, atlasMaterial),
                4: level4Scene,
            },
            {
                1: [
                    applyMaterial(level1ContentSceneA, atlasMaterial),
                    applyMaterial(level1ContentSceneB, atlasMaterial),
                    applyMaterial(level1ContentSceneC, atlasMaterial),
                ],
                2: [
                    applyMaterial(level2ContentSceneA, atlasMaterial),
                    applyMaterial(level2ContentSceneB, atlasMaterial),
                    applyMaterial(level2ContentSceneC, atlasMaterial),
                ],
                3: [
                    applyMaterial(level3ContentSceneA, atlasMaterial),
                    applyMaterial(level3ContentSceneB, atlasMaterial),
                    applyMaterial(level3ContentSceneC, atlasMaterial),
                ],
                4: [
                    level4ContentSceneA,
                    level4ContentSceneB,
                    level4ContentSceneC,
                ],
            },
            {
                Cloud: applyMaterial(cloudScene, atlasMaterial),
                Lvl04Bridge: lvl04BridgeScene,
                Lvl04PirsmA: lvl04PirsmAScene,
                Lvl04PirsmB: lvl04PirsmBScene,
                Lvl04SpaceDonut: lvl04SpaceDonutScene,
                Lvl04SpaceHeart: lvl04SpaceHeartScene,
                Lvl04StarA: lvl04StarAScene,
                Lvl04StarB: lvl04StarBScene,
                LVL2_Clock: applyMaterial(lvl2ClockScene, atlasMaterial),
                LVL2_Geometries: applyMaterial(lvl2GeometriesScene, atlasMaterial),
                LVL2_Globe: lvl2GlobeScene,
                LVL2_MathScience: applyMaterial(lvl2MathScienceScene, atlasMaterial),
                LVL3_Folder: applyMaterial(lvl3FolderScene, atlasMaterial),
                LVL3_Paperplane: applyMaterial(lvl3PaperplaneScene, atlasMaterial),
                LVL3_Window1: applyMaterial(lvl3Window1Scene, atlasMaterial),
                LVL3_Window2: applyMaterial(lvl3Window2Scene, atlasMaterial),
                Rainbow: applyMaterial(rainbowScene, atlasMaterial),
                Teddy: teddyScene,
                ToyCar: toyCarScene,
            },
            trackController,
        ),
        [
            cloudScene,
            lvl04BridgeScene,
            lvl04PirsmAScene,
            lvl04PirsmBScene,
            lvl04SpaceDonutScene,
            lvl04SpaceHeartScene,
            lvl04StarAScene,
            lvl04StarBScene,
            lvl2ClockScene,
            lvl2GeometriesScene,
            lvl2GlobeScene,
            lvl2MathScienceScene,
            lvl3FolderScene,
            lvl3PaperplaneScene,
            lvl3Window1Scene,
            lvl3Window2Scene,
            rainbowScene,
            teddyScene,
            toyCarScene,
            level1Scene,
            level2Scene,
            level3Scene,
            level4Scene,
            level1ContentSceneA,
            level1ContentSceneB,
            level1ContentSceneC,
            level2ContentSceneA,
            level2ContentSceneB,
            level2ContentSceneC,
            level3ContentSceneA,
            level3ContentSceneB,
            level3ContentSceneC,
            level4ContentSceneA,
            level4ContentSceneB,
            level4ContentSceneC,
            atlasMaterial,
            trackController,
        ],
    )
    useEffect(() => {
        return () => {
            disposeObject(platformGroup)
        }
    }, [platformGroup])

    useEffect(() => {
        const syncVisibility = () => {
            if (runState.phase === 'idle') {
                unlockedRegionsRef.current = new Set([1])
            }

            const regionState = runState.trackRegionState
            unlockedRegionsRef.current = getUnlockedPlatformRegionsForState(
                unlockedRegionsRef.current,
                regionState,
            )

            if (!regionState.activeTransition) {
                setPlatformRegionVisibility(
                    platformGroup,
                    unlockedRegionsRef.current,
                )
                return
            }

            const nextRegion = getNextPlatformRegion(regionState.activeRegion)
            if (!nextRegion) {
                return
            }

            if (!unlockedRegionsRef.current.has(nextRegion)) {
                unlockedRegionsRef.current = new Set([
                    ...unlockedRegionsRef.current,
                    nextRegion,
                ])
            }

            setPlatformRegionVisibility(
                platformGroup,
                getVisiblePlatformRegionsForTransition(
                    unlockedRegionsRef.current,
                    regionState.activeRegion,
                ),
            )
        }

        syncVisibility()

        return reaction(
            () => [
                runState.phase,
                runState.trackRegionState.activeRegion,
                runState.trackRegionState.activeTransition,
            ] as const,
            syncVisibility,
        )
    }, [platformGroup, runState])

    return <primitive object={platformGroup} />
}

type PlatformAssetsByRegion = Record<1 | 2 | 3 | 4, THREE.Object3D>
type PlatformContentAssets = Partial<Record<1 | 2 | 3 | 4, readonly [THREE.Object3D, THREE.Object3D, THREE.Object3D]>>
type PlatformContentMatricesByRegion = Partial<Record<1 | 2 | 3 | 4, [THREE.Matrix4[], THREE.Matrix4[], THREE.Matrix4[]]>>
type CustomStartPropAssets = Record<CustomStartPropAssetKey, THREE.Object3D>
type CustomPropMatricesByRegion = Partial<Record<
    1 | 2 | 3 | 4,
    Record<CustomStartPropAssetKey, THREE.Matrix4[]>
>>
type PlatformContentPreviousVariantsByRegion = Record<
    1 | 2 | 3 | 4,
    { 0: 0 | 1 | 2 | null; 1: 0 | 1 | 2 | null }
>

function createPlatforms(
    assetsByRegion: PlatformAssetsByRegion,
    contentAssetsByRegion: PlatformContentAssets,
    customStartPropAssets: CustomStartPropAssets,
    trackController: TrackController,
): THREE.Group {
    const platformMatricesByRegion: Record<1 | 2 | 3 | 4, THREE.Matrix4[]> = {
        1: [],
        2: [],
        3: [],
        4: [],
    }
    const customPlatformMatricesByRegion: Partial<Record<1 | 2 | 3 | 4, THREE.Matrix4[]>> = {}
    const platformContentMatricesByRegion: PlatformContentMatricesByRegion = {}
    const customPropMatricesByRegion: CustomPropMatricesByRegion = {}
    const previousPlatformContentVariantsByRegion: PlatformContentPreviousVariantsByRegion = {
        1: { 0: null, 1: null },
        2: { 0: null, 1: null },
        3: { 0: null, 1: null },
        4: { 0: null, 1: null },
    }
    const trackLength = trackController.getTrackLength()

    for (
        let position = environmentStartOffset;
        position <= trackLength;
        position += environmentSpacing
    ) {
        const regionState = trackController.getTrackRegionState(position)
        if (regionState.activeTransition) {
            continue
        }
        const regionMatrices = platformMatricesByRegion[regionState.activeRegion]
        const leftRotation = createRandomPlatformRotation(
            position,
            regionState.activeRegion,
            0,
        )
        const rightRotation = createRandomPlatformRotation(
            position,
            regionState.activeRegion,
            1,
        )

        const frame = trackController.frameAtPosition(position)
        const transformMatrix = new THREE.Matrix4().compose(
            frame.position,
            frame.cameraQuaternion,
            new THREE.Vector3(1, 1, 1),
        )

        const matrixLeft = transformMatrix.clone().multiply(
            new THREE.Matrix4().makeTranslation(-environmentSideOffset, 0, 0),
        ).multiply(leftRotation)

        const matrixRight = transformMatrix.clone().multiply(
            new THREE.Matrix4().makeTranslation(environmentSideOffset, 0, 0),
        ).multiply(rightRotation)

        regionMatrices.push(matrixLeft)
        regionMatrices.push(matrixRight)

        if (contentAssetsByRegion[regionState.activeRegion]) {
            addPlatformContentInstance(
                getPlatformContentRegionMatrices(
                    platformContentMatricesByRegion,
                    regionState.activeRegion,
                ),
                previousPlatformContentVariantsByRegion[regionState.activeRegion],
                matrixLeft,
                regionState.activeRegion,
                position,
                0,
            )
            addPlatformContentInstance(
                getPlatformContentRegionMatrices(
                    platformContentMatricesByRegion,
                    regionState.activeRegion,
                ),
                previousPlatformContentVariantsByRegion[regionState.activeRegion],
                matrixRight,
                regionState.activeRegion,
                position,
                1,
            )
        }
    }

    appendCustomPlatformAssemblies(
        platformMatricesByRegion,
        platformContentMatricesByRegion,
        contentAssetsByRegion,
        trackController,
    )
    appendCustomPlatformPropPlacements(
        customPlatformMatricesByRegion,
        platformContentMatricesByRegion,
        customPropMatricesByRegion,
    )

    const platformGroup = new THREE.Group()
    platformGroup.name = 'PlatformGroup'

    for (const region of [1, 2, 3, 4] as const) {
        const regionMatrices = platformMatricesByRegion[region]
        if (regionMatrices.length === 0) {
            continue
        }

        const regionGroup = createInstancedAssetGroup(
            assetsByRegion[region],
            regionMatrices,
        )
        regionGroup.name = `PlatformGroup_Region${region}`
        platformGroup.add(regionGroup)
    }

    for (const region of [1, 2, 3, 4] as const) {
        const customRegionMatrices = customPlatformMatricesByRegion[region]
        if (!customRegionMatrices || customRegionMatrices.length === 0) {
            continue
        }

        const customRegionGroup = createInstancedAssetGroup(
            assetsByRegion[region],
            customRegionMatrices,
        )
        customRegionGroup.name = `PlatformGroup_Region${region}_CustomProps`
        platformGroup.add(customRegionGroup)
    }

    for (const region of [1, 2, 3, 4] as const) {
        const contentAssets = contentAssetsByRegion[region]
        const contentMatricesByVariant = platformContentMatricesByRegion[region]
        if (!contentAssets || !contentMatricesByVariant) {
            continue
        }

        for (const [variantIndex, variantMatrices] of contentMatricesByVariant.entries()) {
            if (variantMatrices.length === 0) {
                continue
            }

            const contentGroup = createInstancedAssetGroup(
                contentAssets[variantIndex as 0 | 1 | 2],
                variantMatrices,
            )
            contentGroup.name = `PlatformContentGroup_Region${region}_Variant${variantIndex + 1}`
            platformGroup.add(contentGroup)
        }
    }

    for (const region of [1, 2, 3, 4] as const) {
        const regionPropMatrices = customPropMatricesByRegion[region]
        if (!regionPropMatrices) {
            continue
        }

        for (const assetKey of customStartPropAssetKeys) {
            const propMatrices = regionPropMatrices[assetKey]
            if (!propMatrices || propMatrices.length === 0) {
                continue
            }

            const customPropGroup = createInstancedAssetGroup(
                customStartPropAssets[assetKey],
                propMatrices,
            )
            customPropGroup.name = `PlatformContentGroup_Region${region}_CustomProp_${assetKey}`
            platformGroup.add(customPropGroup)
        }
    }

    applyHeightFogToObject(platformGroup)

    return platformGroup
}

function setPlatformRegionVisibility(
    platformGroup: THREE.Group,
    visibleRegions: ReadonlySet<1 | 2 | 3 | 4>,
) {
    const activeVisibleRegion = getActiveVisibleRegion(visibleRegions)

    for (const region of [1, 2, 3, 4] as const) {
        const visible = visibleRegions.has(region)
        const baseRegionGroup = platformGroup.getObjectByName(
            `PlatformGroup_Region${region}`,
        )
        if (baseRegionGroup) {
            baseRegionGroup.visible = visible
        }

        const customRegionGroup = platformGroup.getObjectByName(
            `PlatformGroup_Region${region}_CustomProps`,
        )
        if (customRegionGroup) {
            customRegionGroup.visible = visible && region === activeVisibleRegion
        }

        if (!baseRegionGroup && !customRegionGroup) {
            setPlatformContentVisibility(
                platformGroup,
                region,
                visible,
                activeVisibleRegion,
            )
            continue
        }
        setPlatformContentVisibility(
            platformGroup,
            region,
            visible,
            activeVisibleRegion,
        )
    }
}

function setPlatformContentVisibility(
    platformGroup: THREE.Group,
    region: 1 | 2 | 3 | 4,
    visible: boolean,
    activeVisibleRegion: 1 | 2 | 3 | 4,
) {
    for (const variantIndex of [1, 2, 3] as const) {
        const contentGroup = platformGroup.getObjectByName(
            `PlatformContentGroup_Region${region}_Variant${variantIndex}`,
        )
        if (!contentGroup) {
            continue
        }
        contentGroup.visible = visible
    }

    for (const assetKey of customStartPropAssetKeys) {
        const customPropGroup = platformGroup.getObjectByName(
            `PlatformContentGroup_Region${region}_CustomProp_${assetKey}`,
        )
        if (!customPropGroup) {
            continue
        }
        customPropGroup.visible = visible && region === activeVisibleRegion
    }
}

function getActiveVisibleRegion(
    visibleRegions: ReadonlySet<1 | 2 | 3 | 4>,
) {
    const orderedRegions = [4, 3, 2, 1] as const
    return orderedRegions.find((region) => visibleRegions.has(region)) ?? 1
}

function getUnlockedPlatformRegionsForState(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    regionState: TrackRegionState,
): Set<1 | 2 | 3 | 4> {
    const nextUnlocked = new Set(unlockedRegions)
    for (const region of [1, 2, 3, 4] as const) {
        if (region <= regionState.activeRegion) {
            nextUnlocked.add(region)
        }
    }
    if (regionState.activeTransition) {
        const nextRegion = getNextPlatformRegion(regionState.activeRegion)
        if (nextRegion) {
            nextUnlocked.add(nextRegion)
        }
    }
    return nextUnlocked
}

function getVisiblePlatformRegionsForTransition(
    unlockedRegions: ReadonlySet<1 | 2 | 3 | 4>,
    activeRegion: 1 | 2 | 3 | 4,
) {
    const visibleRegions = new Set<1 | 2 | 3 | 4>()
    for (const region of unlockedRegions) {
        if (region > activeRegion) {
            visibleRegions.add(region)
        }
    }
    return visibleRegions
}

function createRandomPlatformRotation(
    position: number,
    region: 1 | 2 | 3 | 4,
    side: 0 | 1,
) {
    const seed = Math.abs(Math.sin(position * 12.9898 + region * 78.233 + side * 37.719))
    const turnIndex = Math.floor(seed * 3) + 1
    const angle = turnIndex * (Math.PI / 2)
    return new THREE.Matrix4().makeRotationY(angle)
}

function addPlatformContentInstance(
    matricesByVariant: [THREE.Matrix4[], THREE.Matrix4[], THREE.Matrix4[]],
    previousVariantsBySide: { 0: 0 | 1 | 2 | null; 1: 0 | 1 | 2 | null },
    baseMatrix: THREE.Matrix4,
    region: 1 | 2 | 3 | 4,
    position: number,
    side: 0 | 1,
) {
    const variantIndex = resolvePlatformContentVariantIndex(
        region,
        position,
        side,
        previousVariantsBySide[side],
    )

    const rotationSeed = Math.abs(Math.sin(position * 53.281 + side * 149.771))
    const randomRotation = rotationSeed * Math.PI * 2 

    const randomScale = lerp(0.8, 1.2, Math.random())

    matricesByVariant[variantIndex].push(
        baseMatrix
            .clone()
            .multiply(new THREE.Matrix4().makeRotationY(randomRotation))
            .multiply(new THREE.Matrix4().makeScale(randomScale, randomScale, randomScale),
        ),
    )
    previousVariantsBySide[side] = variantIndex
}

function appendCustomPlatformAssemblies(
    platformMatricesByRegion: Record<1 | 2 | 3 | 4, THREE.Matrix4[]>,
    platformContentMatricesByRegion: PlatformContentMatricesByRegion,
    contentAssetsByRegion: PlatformContentAssets,
    trackController: TrackController,
) {
    const startFrame = trackController.frameAtPosition(environmentStartOffset)
    const forwardAngle = Math.atan2(
        startFrame.tangent.x,
        startFrame.tangent.z,
    )

    customPlatformAssemblies.forEach((assembly) => {
        if (!contentAssetsByRegion[assembly.region]) {
            return
        }

        const regionMatrices = platformMatricesByRegion[assembly.region]
        const contentMatrices = getPlatformContentRegionMatrices(
            platformContentMatricesByRegion,
            assembly.region,
        )

        const center = new THREE.Vector3(
            assembly.centerPosition[0],
            startFrame.position.y + assembly.centerPosition[1],
            assembly.centerPosition[2],
        )

        const arcAngle = Math.PI * 2 * assembly.arcCoverage
        const angleStep = assembly.count > 1
            ? arcAngle / (assembly.count - 1)
            : 0
        const openingCenter = forwardAngle + assembly.openingDirectionOffset
        const arcStart = openingCenter + (Math.PI * 2 - arcAngle) * 0.5

        assembly.contentVariantSequence.forEach((variantIndex, index) => {
            if (index >= assembly.count) {
                return
            }

            const angle = arcStart + angleStep * index
            const platformPosition = new THREE.Vector3(
                center.x + Math.sin(angle) * assembly.radius,
                center.y,
                center.z + Math.cos(angle) * assembly.radius,
            )
            const platformMatrix = createPlatformMatrixFacingCenter(
                platformPosition,
                center,
                assembly.scale,
            )

            regionMatrices.push(platformMatrix)
            contentMatrices[variantIndex].push(platformMatrix.clone())
        })
    })
}

function appendCustomPlatformPropPlacements(
    platformMatricesByRegion: Partial<Record<1 | 2 | 3 | 4, THREE.Matrix4[]>>,
    platformContentMatricesByRegion: PlatformContentMatricesByRegion,
    customPropMatricesByRegion: CustomPropMatricesByRegion,
) {
    customPlatformPropPlacements.forEach((placement) => {
        if (!customPropMatricesByRegion[placement.platformRegion]) {
            customPropMatricesByRegion[placement.platformRegion] = Object.fromEntries(
                customStartPropAssetKeys.map((assetKey) => [assetKey, [] as THREE.Matrix4[]]),
            ) as Record<CustomStartPropAssetKey, THREE.Matrix4[]>
        }

        const cloneCount = Math.max(1, placement.clones ?? 1)
        const cloneSpacingZ = placement.cloneSpacingZ ?? 10

        for (let cloneIndex = 0; cloneIndex < cloneCount; cloneIndex += 1) {
            const clonePosition: readonly [number, number, number] = [
                placement.propPosition[0],
                placement.propPosition[1],
                placement.propPosition[2] - cloneIndex * cloneSpacingZ,
            ]
            const platformMatrix = createPlatformMatrixAtWorldPosition(
                clonePosition,
                placement.platformScale,
            )

            if (!platformMatricesByRegion[placement.platformRegion]) {
                platformMatricesByRegion[placement.platformRegion] = []
            }
            platformMatricesByRegion[placement.platformRegion]!.push(platformMatrix)

            customPropMatricesByRegion[placement.platformRegion]![placement.propAsset].push(
                createOffsetMatrix(
                    platformMatrix,
                    [0, 0, 0],
                    placement.propRotation,
                    placement.propScale,
                ),
            )
        }
    })

    individualContentPlatformPlacements.forEach((placement) => {
        const platformMatrix = createWorldMatrix(
            placement.platformPosition,
            placement.platformRotation,
            placement.platformScale,
        )

        if (!platformMatricesByRegion[placement.platformRegion]) {
            platformMatricesByRegion[placement.platformRegion] = []
        }
        platformMatricesByRegion[placement.platformRegion]!.push(platformMatrix)

        const contentMatrices = getPlatformContentRegionMatrices(
            platformContentMatricesByRegion,
            placement.platformRegion,
        )
        contentMatrices[placement.contentVariant].push(platformMatrix.clone())
    })
}

function createPlatformMatrixFacingCenter(
    platformPosition: THREE.Vector3,
    center: THREE.Vector3,
    scale: number,
) {
    const facingMatrix = new THREE.Matrix4().lookAt(
        platformPosition,
        center,
        new THREE.Vector3(0, 1, 0),
    )
    const platformQuaternion = new THREE.Quaternion().setFromRotationMatrix(
        facingMatrix,
    )

    return new THREE.Matrix4().compose(
        platformPosition,
        platformQuaternion,
        new THREE.Vector3(scale, scale, scale),
    )
}

function createPlatformMatrixAtWorldPosition(
    position: readonly [number, number, number],
    scale: number,
) {
    return new THREE.Matrix4().compose(
        new THREE.Vector3(position[0], position[1], position[2]),
        new THREE.Quaternion(),
        new THREE.Vector3(scale, scale, scale),
    )
}

function createOffsetMatrix(
    baseMatrix: THREE.Matrix4,
    offset: readonly [number, number, number],
    rotation: readonly [number, number, number],
    scale: number,
) {
    const basePosition = new THREE.Vector3()
    const baseQuaternion = new THREE.Quaternion()
    const baseScale = new THREE.Vector3()
    baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

    const worldOffset = new THREE.Vector3(offset[0], offset[1], offset[2])
        .applyQuaternion(baseQuaternion)
    const finalPosition = basePosition.clone().add(worldOffset)
    const finalQuaternion = baseQuaternion.clone().multiply(
        new THREE.Quaternion().setFromEuler(
            new THREE.Euler(rotation[0], rotation[1], rotation[2]),
        ),
    )

    return new THREE.Matrix4().compose(
        finalPosition,
        finalQuaternion,
        new THREE.Vector3(scale, scale, scale),
    )
}

function getPlatformContentRegionMatrices(
    matricesByRegion: PlatformContentMatricesByRegion,
    region: 1 | 2 | 3 | 4,
) {
    const existing = matricesByRegion[region]
    if (existing) {
        return existing
    }

    const created: [THREE.Matrix4[], THREE.Matrix4[], THREE.Matrix4[]] = [
        [],
        [],
        [],
    ]
    matricesByRegion[region] = created
    return created
}

function resolvePlatformContentVariantIndex(
    region: 1 | 2 | 3 | 4,
    position: number,
    side: 0 | 1,
    previousVariant: 0 | 1 | 2 | null): 0 | 1 | 2 {
    const regionSeedOffset = region === 3 ? 413.731 : 0
    const seed = Math.abs(
        Math.sin(position * 31.417 + side * 97.113 + regionSeedOffset),
    )
    const preferredVariant = Math.min(2, Math.floor(seed * 3)) as 0 | 1 | 2

    if (previousVariant == null || preferredVariant !== previousVariant) {
        return preferredVariant
    }

    const alternativeVariants = ([0, 1, 2] as const).filter(
        (variant) => variant !== previousVariant,
    )
    const alternativeSeed = Math.abs(
        Math.sin(position * 67.193 + side * 181.271 + regionSeedOffset),
    )
    const alternativeIndex = Math.min(
        alternativeVariants.length - 1,
        Math.floor(alternativeSeed * alternativeVariants.length),
    )

    return alternativeVariants[alternativeIndex] as 0 | 1 | 2
}

function getNextPlatformRegion(region: 1 | 2 | 3 | 4): 2 | 3 | 4 | null {
    switch (region) {
        case 1:
            return 2
        case 2:
            return 3
        case 3:
            return 4
        case 4:
            return null
    }
}
